import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Brain, TrendingUp, Wallet, Bell, Zap, LineChart, Shield, Check, Star, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import AuthModal from '@/components/AuthModal';
import { MAINTENANCE_MODE } from '@/App';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { toast } from 'sonner';

const Landing = () => {
  const navigate = useNavigate();
  const [hoveredFeature, setHoveredFeature] = useState(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);

  // Maintenance mode handler
  const handleDisabledAction = (actionName) => {
    if (MAINTENANCE_MODE) {
      toast.error('Feature Unavailable', {
        description: `${actionName} is temporarily disabled during maintenance.`
      });
      return true;
    }
    return false;
  };

  const useCases = [
    { text: 'AI Predictions', icon: Brain, position: 'top-20 left-10', delay: 0.2 },
    { text: 'Real-time Tracking', icon: LineChart, position: 'top-32 right-20', delay: 0.4 },
    { text: 'Portfolio Management', icon: Wallet, position: 'top-64 left-1/4', delay: 0.3 },
    { text: 'Price Alerts', icon: Bell, position: 'bottom-40 right-1/3', delay: 0.5 },
    { text: 'Trading Simulator', icon: Zap, position: 'bottom-32 left-20', delay: 0.6 },
    { text: 'Risk Analysis', icon: Shield, position: 'top-40 right-1/4', delay: 0.7 },
  ];

  const features = [
    {
      icon: Brain,
      title: 'AI-Powered Predictions',
      description: 'Advanced machine learning models analyze market trends to forecast Bitcoin prices across multiple timeframes.',
      color: 'from-purple-500 to-violet-600'
    },
    {
      icon: TrendingUp,
      title: 'Real-Time Market Data',
      description: 'Live Bitcoin price updates, historical charts, and 24/7 market monitoring to keep you informed.',
      color: 'from-emerald-500 to-green-600'
    },
    {
      icon: Wallet,
      title: 'Smart Portfolio Tracking',
      description: 'Track your holdings with real-time P&L calculations and performance analytics.',
      color: 'from-blue-500 to-cyan-600'
    },
    {
      icon: Zap,
      title: 'Trading Simulator',
      description: 'Practice trading strategies with paper trading before risking real capital.',
      color: 'from-amber-500 to-orange-600'
    }
  ];

  const pricingPlans = [
    {
      name: 'Starter',
      price: '0',
      period: 'Free Forever',
      features: [
        'Real-time Bitcoin prices',
        'Basic price charts',
        '3 AI predictions per day',
        'Portfolio tracking',
        'Email support'
      ],
      cta: 'Get Started',
      popular: false
    },
    {
      name: 'Pro',
      price: '29',
      period: 'per month',
      features: [
        'Everything in Starter',
        'Unlimited AI predictions',
        'Advanced analytics',
        'Price alerts',
        'Trading simulator',
        'Priority support',
        'Export data'
      ],
      cta: 'Start Free Trial',
      popular: true
    },
    {
      name: 'Enterprise',
      price: '99',
      period: 'per month',
      features: [
        'Everything in Pro',
        'Exchange integration',
        'Custom alerts',
        'API access',
        'White-label option',
        'Dedicated support',
        'Custom features'
      ],
      cta: 'Contact Sales',
      popular: false
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Chen',
      role: 'Day Trader',
      image: 'https://i.pravatar.cc/150?img=1',
      content: 'CryptoPulse Pro AI predictions helped me increase my portfolio by 45% in 3 months. The accuracy is impressive!',
      rating: 5
    },
    {
      name: 'Marcus Rodriguez',
      role: 'Crypto Investor',
      image: 'https://i.pravatar.cc/150?img=12',
      content: 'Best Bitcoin tracking platform I have used. The real-time alerts saved me from a major loss during last month dip.',
      rating: 5
    },
    {
      name: 'Emily Watson',
      role: 'Financial Analyst',
      image: 'https://i.pravatar.cc/150?img=5',
      content: 'The trading simulator is perfect for testing strategies. Went from beginner to confident trader in weeks.',
      rating: 5
    }
  ];

  const faqs = [
    {
      question: 'How accurate are the AI predictions?',
      answer: 'Our AI models achieve approximately 75-85% accuracy on short-term predictions (24hr-7 days) with a 5% price margin. Long-term predictions are provided for trend analysis.'
    },
    {
      question: 'Is my data secure?',
      answer: 'Absolutely. We use bank-level encryption (AES-256) for all data. We never store your exchange API secret keys - they are encrypted on your device and used only for authorized trades.'
    },
    {
      question: 'Can I use real money for trading?',
      answer: 'Pro and Enterprise plans support real exchange integration with Binance. We recommend starting with our paper trading simulator to test strategies risk-free.'
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit cards, debit cards, and digital wallets through Stripe. All transactions are secure and encrypted.'
    },
    {
      question: 'Can I cancel my subscription anytime?',
      answer: 'Yes! You can cancel your subscription at any time from your account dashboard. You will retain access until the end of your billing period.'
    },
    {
      question: 'Do you offer refunds?',
      answer: 'We offer a 14-day money-back guarantee for all paid plans. If you are not satisfied, contact support for a full refund.'
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden">
      {/* Spacer for fixed maintenance banner */}
      {MAINTENANCE_MODE && <div className="h-10 sm:h-12" />}
      
      {/* Navigation - adjusted for maintenance banner */}
      <nav className="fixed w-full z-50 bg-black/50 backdrop-blur-xl border-b border-white/10 top-0" style={MAINTENANCE_MODE ? { top: '2.5rem' } : {}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-purple-600 flex items-center justify-center">
                <LineChart className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold tracking-tight">CryptoPulse Pro</h1>
                <p className="text-xs text-gray-400">AI-Powered Bitcoin Intelligence</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button
                data-testid="nav-dashboard-btn"
                variant="ghost"
                onClick={() => navigate('/dashboard')}
                className="text-gray-300 hover:text-white"
              >
                Dashboard
              </Button>
              <Button
                data-testid="nav-purchase-btn"
                variant="outline"
                onClick={() => handleDisabledAction('Platform purchase') || navigate('/purchase')}
                disabled={MAINTENANCE_MODE}
                className="border-amber-500 text-amber-400 hover:bg-amber-500/10 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Buy Platform
              </Button>
              <Button
                data-testid="nav-get-started-btn"
                onClick={() => handleDisabledAction('Registration') || setAuthModalOpen(true)}
                disabled={MAINTENANCE_MODE}
                className="bg-gradient-to-r from-emerald-500 to-purple-600 hover:from-emerald-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Get Started
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section with Giant Transparent Text */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 via-transparent to-purple-600/10" />
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl" />
        </div>

        {/* Giant Transparent Slogan */}
        <div className="relative z-10 text-center px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
            className="relative"
          >
            <h1 className="text-6xl md:text-8xl lg:text-9xl font-bold tracking-tighter">
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-white/40 to-white/20 stroke-white stroke-2" style={{
                WebkitTextStroke: '1px rgba(255,255,255,0.3)'
              }}>
                FEEL THE
              </span>
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-emerald-400/60 via-purple-400/60 to-emerald-400/60 mt-2" style={{
                WebkitTextStroke: '2px rgba(16,185,129,0.4)'
              }}>
                MARKET'S
              </span>
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-white/40 to-white/20 mt-2" style={{
                WebkitTextStroke: '1px rgba(255,255,255,0.3)'
              }}>
                HEARTBEAT
              </span>
            </h1>
          </motion.div>

          {/* Floating Use Cases */}
          {useCases.map((useCase, index) => (
            <motion.div
              key={useCase.text}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: useCase.delay, duration: 0.6 }}
              className={`absolute ${useCase.position} hidden lg:block`}
            >
              <div className="flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-md border border-white/10 rounded-full hover:bg-white/10 transition-all cursor-pointer">
                <useCase.icon className="w-4 h-4 text-emerald-400" />
                <span className="text-sm font-medium text-gray-300">{useCase.text}</span>
              </div>
            </motion.div>
          ))}

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="text-xl md:text-2xl text-gray-400 mt-8 max-w-2xl mx-auto"
          >
            AI-powered Bitcoin predictions and portfolio management. 
            Trade smarter with real-time insights.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
            className="flex flex-col sm:flex-row gap-4 justify-center mt-12"
          >
            <Button
              data-testid="hero-cta-btn"
              onClick={() => navigate('/dashboard')}
              size="lg"
              className="bg-gradient-to-r from-emerald-500 to-purple-600 hover:from-emerald-600 hover:to-purple-700 text-lg px-8 py-6 h-auto"
            >
              Launch Dashboard
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              data-testid="hero-demo-btn"
              onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
              size="lg"
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10 text-lg px-8 py-6 h-auto"
            >
              Explore Features
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Powered by <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-purple-600">Intelligence</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Advanced features designed to give you the edge in Bitcoin trading
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                onMouseEnter={() => setHoveredFeature(index)}
                onMouseLeave={() => setHoveredFeature(null)}
              >
                <Card className={`p-8 bg-white/5 border-white/10 hover:bg-white/10 transition-all h-full ${
                  hoveredFeature === index ? 'scale-105 shadow-2xl' : ''
                }`}>
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-6`}>
                    <feature.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold mb-3">{feature.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{feature.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Choose Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-purple-600">Plan</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Start free, upgrade as you grow
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className={`p-8 h-full flex flex-col ${
                  plan.popular
                    ? 'bg-gradient-to-br from-emerald-500/20 to-purple-600/20 border-emerald-500/50 relative'
                    : 'bg-white/5 border-white/10'
                }`}>
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-gradient-to-r from-emerald-500 to-purple-600 text-white text-sm font-bold px-4 py-1 rounded-full">
                        MOST POPULAR
                      </span>
                    </div>
                  )}
                  <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                  <div className="mb-6">
                    <span className="text-5xl font-bold">${plan.price}</span>
                    <span className="text-gray-400 ml-2">/ {plan.period}</span>
                  </div>
                  <ul className="space-y-3 mb-8 flex-grow">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    data-testid={`pricing-${plan.name.toLowerCase()}-btn`}
                    onClick={() => handleDisabledAction('Registration') || setAuthModalOpen(true)}
                    disabled={MAINTENANCE_MODE}
                    className={`w-full disabled:opacity-50 disabled:cursor-not-allowed ${
                      plan.popular
                        ? 'bg-gradient-to-r from-emerald-500 to-purple-600 hover:from-emerald-600 hover:to-purple-700'
                        : 'bg-white/10 hover:bg-white/20'
                    }`}
                  >
                    {MAINTENANCE_MODE ? 'Currently Unavailable' : plan.cta}
                  </Button>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof Stats */}
      <section className="py-16 relative bg-gradient-to-r from-emerald-500/10 to-purple-600/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '10,000+', label: 'Active Users' },
              { value: '500K+', label: 'Predictions Made' },
              { value: '82%', label: 'Accuracy Rate' },
              { value: '$2.5M+', label: 'Portfolio Tracked' }
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <p className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-purple-600 mb-2">
                  {stat.value}
                </p>
                <p className="text-gray-400">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Trusted by <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-purple-600">Traders</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              See what our users are saying about CryptoPulse Pro
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="p-6 bg-white/5 border-white/10 h-full">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={`star-${testimonial.name}-${i}`} className="w-5 h-5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-gray-300 mb-6 italic">\"{testimonial.content}\"</p>
                  <div className="flex items-center gap-3">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full"
                    />
                    <div>
                      <p className="font-semibold">{testimonial.name}</p>
                      <p className="text-sm text-gray-400">{testimonial.role}</p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 relative">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Frequently Asked <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-purple-600">Questions</span>
            </h2>
            <p className="text-xl text-gray-400">
              Everything you need to know about CryptoPulse Pro
            </p>
          </motion.div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq) => (
              <motion.div
                key={faq.question}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.05 }}
              >
                <AccordionItem
                  value={faq.question}
                  className="bg-white/5 border border-white/10 rounded-lg px-6"
                >
                  <AccordionTrigger className="text-left hover:no-underline py-4">
                    <span className="font-semibold text-white">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-400 pb-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              </motion.div>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Auth Modal */}
      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        onSuccess={() => navigate('/dashboard')}
      />

      {/* Footer */}
      <footer className="border-t border-white/10 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-purple-600 flex items-center justify-center">
                <LineChart className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold">CryptoPulse Pro</span>
            </div>
            
            <div className="flex flex-col md:flex-row items-center gap-4">
              <Button
                onClick={() => handleDisabledAction('Platform purchase') || navigate('/purchase')}
                disabled={MAINTENANCE_MODE}
                variant="outline"
                className="border-amber-500 text-amber-400 hover:bg-amber-500/10 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {MAINTENANCE_MODE ? 'Purchases Unavailable' : 'Buy Platform ($297-$4,997)'}
              </Button>
              <Button
                onClick={() => handleDisabledAction('Registration') || setAuthModalOpen(true)}
                disabled={MAINTENANCE_MODE}
                className="bg-gradient-to-r from-emerald-500 to-purple-600 hover:from-emerald-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {MAINTENANCE_MODE ? 'Registration Unavailable' : 'Try Free'}
                {!MAINTENANCE_MODE && <ArrowRight className="w-4 h-4 ml-2" />}
              </Button>
            </div>
            
            <div className="flex flex-col items-center md:items-end gap-2">
              <div className="flex gap-4 text-sm">
                <button
                  onClick={() => navigate('/privacy-policy')}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  Privacy Policy
                </button>
                <button
                  onClick={() => navigate('/terms-of-service')}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  Terms of Service
                </button>
              </div>
              <p className="text-gray-400 text-sm">
                © 2026 CryptoPulse Pro. AI-Powered Bitcoin Intelligence.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;